import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './MissionTransition.css';

/**
 * The beat between two linked missions of a storyline (backend plan.save_storyline):
 *   time  - same place, later: the room dims and a clock sweeps from one mission's time to the next,
 *           then the next scene fades in.
 *   place - you move to another room: a first-person walk. The same procedural passage as the Tamil
 *           Cave's tunnel (tamil-cave/CaveTunnelFPV - four converging faces scrolling toward the
 *           viewer, with a walking sway), textured from the room you're leaving; on arrival the next
 *           room's own background grows in from the dark opening ahead.
 * No new art and no AI calls - both are built from the two missions' backgrounds. Skippable.
 */
const API_BASE = process.env.REACT_APP_API_URL || '';
const asset = (src) => (src && src.startsWith('/game-builder-assets') ? `${API_BASE}${src}` : src);
const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const minutesOf = (hhmm) => {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

function Clock({ from, to, run }) {
  const start = minutesOf(from) ?? 0;
  let end = minutesOf(to);
  if (end === null) end = start + 90;
  if (end <= start) end += 24 * 60;                    // past midnight
  const [shown, setShown] = useState(start);
  useEffect(() => {
    if (!run) return undefined;
    const t = setTimeout(() => setShown(end), 60);
    return () => clearTimeout(t);
  }, [run, end]);
  const hour = ((shown / 60) % 12) * 30;
  const minute = (shown % 60) * 6 + Math.floor(shown / 60) * 360;   // the minute hand keeps turning
  const label = (m) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  return (
    <div className="mt-clock" role="img" aria-label={`From ${label(start)} to ${label(end % (24 * 60))}`}>
      <svg viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="46" className="mt-clock-face" />
        {Array.from({ length: 12 }, (_, i) => (
          <line key={i} x1="50" y1="8" x2="50" y2={i % 3 === 0 ? 16 : 12} className="mt-clock-tick" transform={`rotate(${i * 30} 50 50)`} />
        ))}
        <line x1="50" y1="50" x2="50" y2="28" className="mt-clock-hour" style={{ transform: `rotate(${hour}deg)` }} />
        <line x1="50" y1="50" x2="50" y2="16" className="mt-clock-minute" style={{ transform: `rotate(${minute}deg)` }} />
        <circle cx="50" cy="50" r="3" className="mt-clock-pin" />
      </svg>
      <div className="mt-clock-times">{label(start)} → {label(end % (24 * 60))}</div>
    </div>
  );
}

export default function MissionTransition({ link, fromSrc, toSrc, nextName, onDone }) {
  const quick = reducedMotion();
  const [stage, setStage] = useState('go');        // 'go' (time passes / walking) -> 'arrive' (next scene)
  const finished = useRef(false);
  const done = () => { if (!finished.current) { finished.current = true; onDone(); } };

  useEffect(() => {
    const goMs = quick ? 900 : link.type === 'place' ? 3200 : 3600;
    const arriveMs = quick ? 500 : 1400;
    const t1 = setTimeout(() => setStage('arrive'), goMs);
    const t2 = setTimeout(done, goMs + arriveMs);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const from = { backgroundImage: `url(${asset(fromSrc)})` };
  const to = { backgroundImage: `url(${asset(toSrc)})` };
  return createPortal(
    <div className={`mt-stage mt-${link.type} is-${stage} ${quick ? 'is-quick' : ''}`} role="dialog" aria-modal="true"
      aria-label={link.type === 'place' ? `Moving: ${link.label}` : `Time passes: ${link.label}`}>
      {link.type === 'place' ? (
        stage === 'go' ? (
          <div className="mt-walk">
            <div className="mt-void" />
            {['ceiling', 'floor', 'left', 'right'].map((side) => (
              <div key={side} className={`mt-face mt-face-${side}`}><div className="mt-face-tex" style={from} /></div>
            ))}
            <div className="mt-void-overlay" />
          </div>
        ) : (
          <div className="mt-arrive-grow" style={to} />
        )
      ) : (
        <>
          <div className={`mt-scene ${stage === 'arrive' ? 'is-out' : ''}`} style={from} />
          <div className={`mt-scene mt-scene-next ${stage === 'arrive' ? 'is-in' : ''}`} style={to} />
          <div className={`mt-dusk ${stage === 'arrive' ? 'is-out' : ''}`} />
          {stage === 'go' && <Clock from={link.from_time} to={link.to_time} run />}
        </>
      )}
      <div className="mt-caption">
        <span className="mt-caption-icon" aria-hidden="true">{link.type === 'place' ? '🚶' : '🕐'}</span>
        <span>{link.label || (link.type === 'place' ? nextName : 'Later')}</span>
      </div>
      <button type="button" className="mt-skip" onClick={done}>Skip ›</button>
    </div>,
    document.body
  );
}
